import React from 'react';
import styled, { keyframes } from 'styled-components';
import type { TurnThinking, TurnStatus } from '../../../hooks/useWorkspace';

const pulse = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(1); }
  50%      { opacity: 1;    transform: scale(1.4); }
`;

const Wrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-family: inherit;
  line-height: 1.2;
`;

const Dot = styled.span`
  display: inline-block;
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  background: var(--accent, #4eb8ad);
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

const Label = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const Summary = styled.span`
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60ch;
`;

interface TurnThinkingStripProps {
  thinking?: TurnThinking;
  status: TurnStatus;
}

/**
 * Soft pill that renders the WMA's most recent ``exchange.thinking`` summary
 * above the activity strip. Only visible while the turn is still in flight
 * (``running`` or ``awaiting_input``); the state cleanup in ``useWorkspace``
 * also clears ``thinking`` once concrete activity arrives or the exchange
 * completes, so the pill naturally vanishes when work begins.
 */
const TurnThinkingStrip: React.FC<TurnThinkingStripProps> = ({
  thinking,
  status,
}) => {
  if (!thinking || !thinking.summary) return null;
  if (status !== 'running' && status !== 'awaiting_input') return null;

  return (
    <Wrap role="status" aria-live="polite" aria-label="Agent is thinking">
      <Dot aria-hidden="true" />
      <Label>Thinking</Label>
      <Summary>{thinking.summary}</Summary>
    </Wrap>
  );
};

export default TurnThinkingStrip;
